import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { IconEdit } from '@components/icons/edit';
import { IconWebhook } from '@components/icons/webhook';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { StatusOptionLabel } from '@components/status-option-label';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { endpointColorMap } from '../common';
import { useCreateIntegrationWebhook, useUpdateIntegrationWebhook } from '../integrations-hooks';
import {
  endpointMethods,
  type IntegrationWebhook,
  type IntegrationWebhookCreateType,
} from '../integrations-types';

export const useIntegrationWebhookCreateSidebar = () =>
  useSidebar(IntegrationWebhookCreateSidebar, { variant: 'REGULAR' });

const Form = createForm<IntegrationWebhookCreateType>();

const IntegrationWebhookCreateSidebar = ({
  integrationId,
  webhook,
}: {
  integrationId: string;
  webhook?: IntegrationWebhook;
}) => {
  const { t } = useScopedTranslate(locale);
  const createMutation = useCreateIntegrationWebhook();
  const updateMutation = useUpdateIntegrationWebhook();
  const isEdit = Boolean(webhook);
  const handleSubmit = async (data: IntegrationWebhookCreateType) => {
    if (webhook) {
      await updateMutation.mutateAsync({ data, shortId: webhook.shortId });
    } else {
      await createMutation.mutateAsync({
        ...data,
        integration: integrationId,
        url: data.url ?? 'http://unsubscribable-url.50skills.com',
      });
    }
    hideSidebar();
  };
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const isSubscribable = formStore.useFieldValue('isSubscribable') as boolean;
  const isLoading = createMutation.isLoading || updateMutation.isLoading;
  const allowedMethods = endpointMethods.filter(
    (method) => method !== 'GET' && method !== 'DELETE'
  );

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={isEdit ? IconEdit : IconWebhook}
        iconColor="elementBlue100"
        title={isEdit ? t('editWebhook') : t('addWebhook')}
      />
      <Sidebar.Content>
        <Form onSubmit={handleSubmit} store={formStore}>
          <Form.TextField
            id="name"
            label={t('name')}
            validators={['required']}
            defaultValue={webhook?.name ?? ''}
          />
          <Form.Switch
            id="isSubscribable"
            label={t('subscribeWithExternalUrl')}
            defaultValue={webhook?.isSubscribable ?? true}
            isDisabled={Boolean(webhook)}
          />
          <Alert variant="info">
            <Text
              color="indigo100"
              font="regular14"
              text={isSubscribable ? t('subscribableDescription') : t('unsubscribableDescription')}
            />
          </Alert>
          {isSubscribable && (
            <>
              <Form.TextField
                id="url"
                label={t('url')}
                validators={['valid-http-url', 'required']}
                defaultValue={webhook?.url ?? ''}
                isDisabled={webhook?.webhookCreated}
              />
              <Form.Select
                id="method"
                label={t('method')}
                validators={['required']}
                options={allowedMethods.map((endpointMethod) => ({
                  label: (
                    <StatusOptionLabel
                      text={endpointMethod}
                      color={endpointColorMap[endpointMethod]}
                    />
                  ),
                  value: endpointMethod,
                }))}
                isClearable={false}
                defaultValue={webhook?.method ?? 'POST'}
                isDisabled={webhook?.webhookCreated}
              />
              <Form.JsonEditor
                id="exampleData"
                label={t('requestValues')}
                initialValue={webhook?.exampleData}
                isDisabled={webhook?.webhookCreated}
              />
            </>
          )}

          <Form.JsonEditor
            id="payloadData"
            label={t('payloadValues')}
            initialValue={webhook?.payloadData}
          />
          <Sidebar.Footer>
            <Form.Actions>
              <Button text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
              <Form.Submit
                variant="primary-blue"
                text={isEdit ? t('core.save') : t('addWebhook')}
                isDisabled={!isDirty || isLoading}
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const locale = {
  en: {
    addWebhook: 'Add Webhook',
    editWebhook: 'Edit Webhook',
    name: 'Name',
    url: 'URL',
    method: 'Method',
    requestValues: 'Request values used to subscribe',
    payloadValues: 'Payload values the webhook returns',
    subscribeWithExternalUrl: 'Subscribe using external url',
    subscribableDescription:
      "Automate the process of subscribing to a webhook by calling an external URL. This way, users won't need to manually insert the unique webhook URL into actions; it will be done automatically",
    unsubscribableDescription:
      'Create a webhook instance to get a unique webhook url that can then be used in other actions',
  },
};
